import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Card, Col, DatePicker, Descriptions, Drawer, Flex, Form, Input, InputNumber, Popconfirm, Row, Select, Table, Tag, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { Link } from "react-router";
import { api, unwrap, type Schemas } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { SupplierSelect, rupiahInput } from "../procurement/common";

type Credit = Schemas["SupplierCredit"];
type Use = NonNullable<Credit["uses"]>[number];
const STATUSES = ["open", "used", "all"] as const;
const DAY = "YYYY-MM-DD";

export function CreditStatusTag({ status }: { status: Credit["status"] }) {
  const { t } = useI18n();
  return <Tag color={status === "open" ? "processing" : status === "used" ? "success" : "default"}>{t(`credit.status.${status}`)}</Tag>;
}

/**
 * Keuangan > Nota kredit supplier (Plan §6.19): what suppliers owe back for
 * goods returned to them, used on their bills or refunded into an account.
 */
export function Credits() {
  const { t, formatDate, formatMoney } = useI18n();
  const [status, setStatus] = useUrlFilter<(typeof STATUSES)[number]>("status", "open", STATUSES);
  const [supplierId, setSupplierId] = useUrlFilter<string>("supplierId", "");
  const [open, setOpen] = useUrlFilter<string>("open", "");
  const list = useQuery({
    queryKey: ["finance", "credits", status, supplierId],
    queryFn: () =>
      unwrap(
        api.GET("/v1/finance/credits", {
          params: { query: { ...(status === "all" ? {} : { status }), ...(supplierId ? { supplierId } : {}) } },
        }),
      ),
  });
  const items = list.data?.items ?? [];
  const remaining = items.reduce((s, c) => s + (c.status === "open" ? c.remaining : 0), 0);

  return (
    <>
      <Typography.Title level={2} style={{ margin: "0 0 8px", fontSize: 28 }}>
        {t("credit.title")}
      </Typography.Title>
      <Typography.Paragraph type="secondary">{t("credit.subtitle")}</Typography.Paragraph>
      <Flex gap={12} wrap align="center" style={{ marginBottom: 16 }}>
        <Select<(typeof STATUSES)[number]>
          value={status}
          onChange={setStatus}
          style={{ width: 200 }}
          options={STATUSES.map((s) => ({ value: s, label: s === "all" ? t("list.allStatuses") : t(`credit.status.${s}`) }))}
        />
        <SupplierSelect value={supplierId || undefined} onChange={(id) => setSupplierId(id ?? "")} allowClear style={{ width: 240 }} />
        <div style={{ flex: 1 }} />
        {remaining > 0 && (
          <Typography.Text>
            {t("credit.remainingTotal")} <Typography.Text strong className="z-num">{formatMoney(remaining)}</Typography.Text>
          </Typography.Text>
        )}
      </Flex>
      <Table<Credit>
        rowKey="id"
        loading={list.isPending}
        dataSource={items}
        pagination={items.length > 50 ? { pageSize: 50, showSizeChanger: false } : false}
        locale={{ emptyText: t("credit.empty") }}
        onRow={(c) => ({ onClick: () => setOpen(c.id), style: { cursor: "pointer" } })}
        columns={[
          { title: t("credit.number"), dataIndex: "number", width: 130, render: (v: string) => <Typography.Text strong className="z-num">{v}</Typography.Text> },
          { title: t("po.supplier"), dataIndex: "supplierName" },
          { title: t("credit.return"), dataIndex: "returnNumber", width: 130, render: (v: string) => <span className="z-num">{v || "—"}</span> },
          { title: t("credit.issuedOn"), dataIndex: "issuedOn", width: 120, render: (v: string) => formatDate(v) },
          { title: t("credit.amount"), dataIndex: "amount", width: 140, align: "right", render: (v: number) => <span className="z-num">{formatMoney(v)}</span> },
          { title: t("credit.remaining"), dataIndex: "remaining", width: 140, align: "right", render: (v: number) => <span className="z-num">{formatMoney(v)}</span> },
          { title: t("common.status"), dataIndex: "status", width: 120, render: (v: Credit["status"]) => <CreditStatusTag status={v} /> },
        ]}
      />
      <CreditDrawer id={open} onClose={() => setOpen("")} />
    </>
  );
}

function CreditDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const { t, formatDate, formatMoney } = useI18n();
  const { can } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const credit = useQuery({
    queryKey: ["finance", "credits", "detail", id],
    queryFn: () => unwrap(api.GET("/v1/finance/credits/{creditID}", { params: { path: { creditID: id } } })),
    enabled: !!id,
  });
  const c = credit.data;
  const saved = (x: Credit) => {
    queryClient.setQueryData(["finance", "credits", "detail", id], x);
    for (const k of [["finance"], ["vendor-returns"]]) void queryClient.invalidateQueries({ queryKey: k });
  };
  const voidRefund = useMutation({
    mutationFn: (entryID: string) => unwrap(api.POST("/v1/finance/credits/{creditID}/refunds/{entryID}/void", { params: { path: { creditID: id, entryID } } })),
    onSuccess: saved,
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Drawer open={!!id} onClose={onClose} size="large" title={c ? `${c.number} · ${c.supplierName}` : ""} destroyOnHidden>
      {c && (
        <>
          <Descriptions
            column={1}
            size="small"
            items={[
              { key: "status", label: t("common.status"), children: <CreditStatusTag status={c.status} /> },
              {
                key: "ret",
                label: t("credit.return"),
                children: c.vendorReturnId ? <Link to={`/app/procurement/returns/${c.vendorReturnId}`}>{c.returnNumber}</Link> : "—",
              },
              { key: "on", label: t("credit.issuedOn"), children: formatDate(c.issuedOn) },
              { key: "amount", label: t("credit.amount"), children: <span className="z-num">{formatMoney(c.amount)}</span> },
              { key: "used", label: t("credit.used"), children: <span className="z-num">{formatMoney(c.used)}</span> },
              { key: "left", label: t("credit.remaining"), children: <Typography.Text strong className="z-num">{formatMoney(c.remaining)}</Typography.Text> },
            ]}
          />
          <Typography.Paragraph type="secondary" style={{ marginTop: 12 }}>
            {t("credit.useHelp")}
          </Typography.Paragraph>
          <Table<Use>
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={c.uses ?? []}
            locale={{ emptyText: t("credit.noUses") }}
            columns={[
              {
                title: t("credit.use"),
                render: (_, u) =>
                  u.kind === "bill" ? (
                    <span>
                      {t("credit.use.bill")} <Link to={`/app/finance/bills/${u.billId}`}>{u.billNumber}</Link>
                    </span>
                  ) : (
                    <span>
                      {t("credit.use.refund")} · {u.accountName}
                    </span>
                  ),
              },
              { title: t("credit.on"), dataIndex: "on", width: 110, render: (v: string) => formatDate(v) },
              {
                title: t("credit.amount"),
                dataIndex: "amount",
                width: 130,
                align: "right",
                render: (v: number, u) =>
                  u.status === "void" ? <Typography.Text delete type="secondary" className="z-num">{formatMoney(v)}</Typography.Text> : <span className="z-num">{formatMoney(v)}</span>,
              },
              {
                width: 90,
                render: (_, u) =>
                  u.kind === "refund" &&
                  u.status === "posted" &&
                  can("finance.manage") && (
                    <Popconfirm title={t("credit.voidRefundConfirm")} onConfirm={() => voidRefund.mutate(u.id)}>
                      <Button size="small" type="link" danger>
                        {t("credit.voidRefund")}
                      </Button>
                    </Popconfirm>
                  ),
              },
            ]}
          />
          {c.status === "open" && can("finance.manage") && <RefundForm credit={c} onSaved={saved} />}
        </>
      )}
    </Drawer>
  );
}

type RefundValues = { accountId?: string; receivedOn: Dayjs; amount: number; reference?: string; note?: string };

function RefundForm({ credit, onSaved }: { credit: Credit; onSaved: (c: Credit) => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const [form] = Form.useForm<RefundValues>();
  const accounts = useQuery({ queryKey: ["finance", "cash", "accounts"], queryFn: () => unwrap(api.GET("/v1/finance/cash/accounts")) });
  const refund = useMutation({
    mutationFn: (v: RefundValues) =>
      unwrap(
        api.POST("/v1/finance/credits/{creditID}/refunds", {
          params: { path: { creditID: credit.id } },
          body: { accountId: v.accountId!, receivedOn: v.receivedOn.format(DAY), amount: v.amount, reference: v.reference?.trim() ?? "", note: v.note?.trim() ?? "" },
        }),
      ),
    onSuccess: (c) => {
      onSaved(c);
      form.resetFields();
      void message.success(t("credit.refunded"));
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  return (
    <Card size="small" title={t("credit.refundTitle")} style={{ marginTop: 16 }}>
      <Form form={form} layout="vertical" requiredMark={false} initialValues={{ receivedOn: dayjs(), amount: credit.remaining }} onFinish={(v) => refund.mutate(v)}>
        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item name="accountId" label={t("credit.into")} rules={[{ required: true, message: t("field.required") }]}>
              <Select
                loading={accounts.isPending}
                options={(accounts.data?.items ?? []).filter((a) => a.active).map((a) => ({ value: a.id, label: `${a.name}${a.accountNo ? ` · ${a.accountNo}` : ""}` }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="receivedOn" label={t("credit.receivedOn")} rules={[{ required: true, message: t("field.required") }]}>
              <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} allowClear={false} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="amount" label={t("credit.amount")} rules={[{ required: true, message: t("field.required") }]}>
              <InputNumber<number> min={1} max={credit.remaining} precision={0} prefix="Rp" style={{ width: "100%" }} {...rupiahInput} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="reference" label={t("bill.reference")}>
              <Input maxLength={120} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="note" label={t("supplier.note")}>
              <Input maxLength={500} />
            </Form.Item>
          </Col>
        </Row>
        <Button type="primary" htmlType="submit" loading={refund.isPending}>
          {t("credit.recordRefund")}
        </Button>
      </Form>
    </Card>
  );
}
